import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      // Verify token is still valid and get latest approval status
      authAPI.me()
        .then((res) => {
          setUser(res.data);
          localStorage.setItem('user', JSON.stringify(res.data));
        })
        .catch(() => {
          logout();
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const response = await authAPI.login({ email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name, role, invitationCode = null) => {
    const response = await authAPI.register({ 
      email, 
      password, 
      name, 
      role,
      invitation_code: invitationCode 
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const res = await authAPI.me();
      setUser(res.data);
      localStorage.setItem('user', JSON.stringify(res.data));
      return res.data;
    } catch (error) {
      logout();
      return null;
    }
  };

  // Check if user can approve others (head_coach or trainer)
  const canApproveUsers = user?.role === 'head_coach' || user?.role === 'trainer';
  
  // Check if user can invite others (head_coach AND trainer - same as approve)
  const canInviteUsers = user?.role === 'head_coach' || user?.role === 'trainer';
  
  // Check if user has full access
  const hasFullAccess = ['head_coach', 'coordinator', 'trainer'].includes(user?.role);
  
  // Check if user is approved
  const isApproved = user?.approved !== false; // Legacy users without approved field are approved

  return (
    <AuthContext.Provider value={{ 
      user, 
      login, 
      register, 
      logout, 
      loading, 
      refreshUser,
      canApproveUsers,
      canInviteUsers,
      hasFullAccess,
      isApproved 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
